import React from 'react';
import styled from 'styled-components';
import { FaApple, FaGooglePlay } from 'react-icons/fa';

interface AppStoreBadgesProps {
  variant?: 'dark' | 'light';
  size?: 'sm' | 'md' | 'lg';
  direction?: 'row' | 'column';
  className?: string;
}

const APP_STORE_URL = 'https://apps.apple.com/us/app/pasur-online-play-score/id6476008595';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.vakilventures.pasur';

const sizeMap = {
  sm: { padding: '0.5rem 1rem', fontSize: '0.85rem', iconSize: '1.2rem', gap: '0.4rem' },
  md: { padding: '0.75rem 1.5rem', fontSize: '1rem', iconSize: '1.5rem', gap: '0.5rem' },
  lg: { padding: '1rem 2rem', fontSize: '1.1rem', iconSize: '1.75rem', gap: '0.6rem' },
};

const Container = styled.div<{ direction: 'row' | 'column' }>`
  display: flex;
  flex-direction: ${props => props.direction};
  gap: 1rem;
  align-items: center;
  justify-content: center;

  @media (max-width: 480px) {
    flex-direction: column;
    width: 100%;
  }
`;

const StoreButton = styled.a<{
  variant: 'dark' | 'light';
  size: 'sm' | 'md' | 'lg';
  store: 'apple' | 'google';
}>`
  display: inline-flex;
  align-items: center;
  gap: ${props => sizeMap[props.size].gap};
  padding: ${props => sizeMap[props.size].padding};
  border-radius: 12px;
  text-decoration: none;
  font-weight: 600;
  font-size: ${props => sizeMap[props.size].fontSize};
  transition: all 0.2s ease;
  border: 2px solid transparent;

  background: ${props => props.variant === 'dark' ? '#1a1a2e' : '#ffffff'};
  color: ${props => props.variant === 'dark' ? '#ffffff' : '#1a1a2e'};
  border-color: ${props => props.variant === 'dark' ? '#1a1a2e' : '#e0e0e0'};

  svg {
    font-size: ${props => sizeMap[props.size].iconSize};
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
    background: ${props => props.variant === 'dark' ? '#2a2a3e' : '#f5f5f5'};
  }

  &:active {
    transform: translateY(-1px);
  }

  @media (max-width: 480px) {
    width: 100%;
    justify-content: center;
  }
`;

const ButtonContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.2;
`;

const SmallText = styled.span`
  font-size: 0.7em;
  font-weight: 400;
  opacity: 0.8;
`;

const MainText = styled.span`
  font-weight: 600;
`;

const AppStoreBadges: React.FC<AppStoreBadgesProps> = ({
  variant = 'dark',
  size = 'md',
  direction = 'row',
  className,
}) => {
  return (
    <Container direction={direction} className={className}>
      <StoreButton
        href={APP_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        variant={variant}
        size={size}
        store="apple"
        aria-label="Download on the App Store"
      >
        <FaApple />
        <ButtonContent>
          <SmallText>Download on the</SmallText>
          <MainText>App Store</MainText>
        </ButtonContent>
      </StoreButton>

      <StoreButton
        href={PLAY_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        variant={variant}
        size={size}
        store="google"
        aria-label="Get it on Google Play"
      >
        <FaGooglePlay />
        <ButtonContent>
          <SmallText>Get it on</SmallText>
          <MainText>Google Play</MainText>
        </ButtonContent>
      </StoreButton>
    </Container>
  );
};

export default AppStoreBadges;
